import { useCallback, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EventsHeader } from "@/components/admin/events/EventsHeader";
import { EventBoardDialogs } from "@/components/admin/events/EventBoardDialogs";
import { EventsFilterBar } from "@/components/admin/events/filters/EventsFilterBar";
import { EventsQuickViews } from "@/components/admin/events/filters/EventsQuickViews";
import { EventsSummary } from "@/components/admin/events/filters/EventsSummary";
import { EventsPipelineView } from "@/components/admin/events/pipeline/EventsPipelineView";
import { EventsListView } from "@/components/admin/events/list/EventsListView";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { useEventBoardActions } from "@/hooks/useEventBoardActions";
import { PIPELINE_CLOSED_LIMIT, useEventsPipeline, type EventsPipelineData } from "@/hooks/useEventsPipeline";
import { useEventsViewState } from "@/hooks/useEventsViewState";
import { useToday } from "@/hooks/useToday";
import { toBoardRows } from "@/lib/event-board";
import {
  closedWindow,
  matchesFilters,
  openCapMayHide,
  type DateWindow,
  type QuickViewKey,
} from "@/lib/event-filters";
import { CLOSED_STAGES, OPEN_STAGES, type EventStage, type EventsView } from "@/lib/event-pipeline";

/** A note when a load cap can hide rows from the current filters. */
function CappedNotes({
  data,
  stages,
  dateWindow,
}: {
  data: EventsPipelineData | undefined;
  stages: EventStage[];
  dateWindow: DateWindow;
}) {
  if (!data) return null;
  const closedPicked = stages.some((s) => CLOSED_STAGES.includes(s));
  return (
    <>
      {openCapMayHide(data.capped, data.openBoundary, dateWindow, stages) && (
        <p className="text-sm text-muted-foreground">
          Not every open event is loaded, so this view may be missing some. Search to narrow.
        </p>
      )}
      {data.closedCapped && closedPicked && (
        <p className="text-sm text-muted-foreground">
          Showing the newest {PIPELINE_CLOSED_LIMIT} of {data.closedCount.toLocaleString()} closed events. Narrow the
          dates.
        </p>
      )}
    </>
  );
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="space-y-3 py-12 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Couldn't load events.</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

function BoardSkeleton({ view }: { view: EventsView }) {
  if (view === "list") {
    return (
      <div className="space-y-3 rounded-[10px] border bg-card p-6" aria-busy="true" aria-label="Loading events">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex gap-3 overflow-hidden" aria-busy="true" aria-label="Loading events">
      {OPEN_STAGES.slice(0, 5).map((s) => (
        <Skeleton key={s} className="h-64 w-[220px] shrink-0 rounded-[10px]" />
      ))}
    </div>
  );
}

/**
 * /admin/events (#1557): one header, quick views, filters and a summary over
 * the same filtered events, shown as the Pipeline or the List.
 */
export default function EventsAdminPage() {
  const today = useToday();
  const state = useEventsViewState(today);
  const { filters, applyQuickView } = state;
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const dateWindow = useMemo(() => closedWindow(filters, today), [filters, today]);
  const { data, isLoading, isError, refetch } = useEventsPipeline({
    search: debounced,
    window: dateWindow,
    includePartial: filters.incomplete,
  });
  const rows = useMemo(() => toBoardRows(data, today, Date.now()), [data, today]);
  const visible = useMemo(() => rows.filter((r) => matchesFilters(r, filters, today)), [rows, filters, today]);
  const { handlers, pending, dialogs } = useEventBoardActions();

  // A quick view answers its question over every event, so it clears the search.
  const onQuickView = useCallback(
    (k: QuickViewKey) => {
      setSearch("");
      applyQuickView(k);
    },
    [applyQuickView],
  );
  const retry = useCallback(() => void refetch(), [refetch]);

  return (
    <DashboardLayout>
      <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-3.5">
        <EventsHeader
          view={state.view}
          onViewChange={state.setView}
          search={search}
          onSearchChange={setSearch}
          onNewEvent={handlers.newEvent}
        />
        <EventsQuickViews
          rows={rows}
          today={today}
          active={state.quick}
          onApply={onQuickView}
          messagesUnavailable={!!data?.messagesUnavailable}
        />
        <EventsFilterBar
          rows={rows}
          filters={filters}
          today={today}
          isDefault={state.isDefault}
          updateFilters={state.updateFilters}
          clearFilters={state.clearFilters}
          clearIncomplete={state.clearIncomplete}
        />
        <EventsSummary rows={visible} />
        <CappedNotes data={data} stages={filters.stages} dateWindow={dateWindow} />
        {isError ? (
          <LoadError onRetry={retry} />
        ) : isLoading ? (
          <BoardSkeleton view={state.view} />
        ) : state.view === "list" ? (
          <EventsListView rows={visible} today={today} onOpenDetail={handlers.openDetail} />
        ) : (
          <EventsPipelineView
            rows={visible}
            stages={filters.stages}
            handlers={handlers}
            pending={pending}
            wrapUpUnavailable={!!data?.wrapUpUnavailable}
          />
        )}
      </div>
      <EventBoardDialogs {...dialogs} />
    </DashboardLayout>
  );
}
